import { Injectable } from '@angular/core';
import { AreaCoreService } from './area-core.service';
import { ClusterService } from '../cluster/cluster.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { Area, AreaLocation, AreaUpdateOptions } from './area.interface';
import { BuildingService } from '../building/building.service';
import { ModuleService } from '../module/module.service';
import { ProjectModule } from '../module/project-module.interface';
import { Observable } from 'rxjs';
import { ElementService } from '../element/element.service';
import { ProjectElement } from '../element/project-element.interface';
import { Project } from '../project/project.interface';
import { ModalService } from '../../services/modal/modal.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Cluster } from '../cluster/cluster.interface';
import { BasicAreaService } from '../basic-area/basic-area.service';
import { Building } from '../building/building.interface';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Area service.
 *
 * The public interface for the AreaCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class AreaService extends AreaCoreService {

  // public area;

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected buildingService: BuildingService,
    protected clusterService: ClusterService,
    private elementService: ElementService,
    private modalService: ModalService,
    private moduleService: ModuleService,
    protected projectService: ProjectService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, apiLegacyService, buildingService, clusterService, projectService);
  }

  /**
   * Add area.
   *
   * @todo Try to remove dependency on cluster param.
   *
   * @param buildingId The id of the parent building
   * @param clusterId The id of the parent cluster.
   * @param [template={}] The new area template, optional.
   * @param [confirm=false] Whether to show a modal.
   */
  public add(buildingId: Building['_id'], clusterId: Cluster['_id'], template: Partial<Area> = {}, confirm: boolean = false): void {
    template = {
      buildingId: buildingId,
      clusterId: clusterId,
      ...template
    };
    if (confirm) {
      this.modalService.openAddArea(template).afterClosed().subscribe((area: Partial<Area>) => {
        if (area) {
          this.add(template.buildingId, template.clusterId, area);
        }
      });
    } else {
      this._add(template).subscribe(response => {
        this.toastService.open(this.translateService.get('areaadded'));
      });
    }
  }

  /**
   * Delete area.
   *
   * @note When deleting with a confirm modal, it is not needed to subscribe.
   *
   * @param area The area to delete.
   * @param [confirm=false] Whether to show confirm modal before deletion.
   * @returns Observable with boolean upon deletion. Can be omitted when deleting by modal.
   */
  public delete(area: Area, confirm: boolean = false, options?: AreaUpdateOptions): Observable<boolean> {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('areaDeleteTitle'),
        content: this.translateService.get('areyousuredeletingarea'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(area, false, options).subscribe();
        }
      });
    } else {
      return new Observable(observer => {
        this._delete(area, options).subscribe(() => {
          this.toastService.open(this.translateService.get('areaDeleted'));
          observer.next(true);
          observer.complete();
        });
      });
    }
  }

  /**
   * Get a list of elements in the area.
   *
   * Makes use of the `getRequirements()` method to get a list of *all* elements
   * in the area and parent basic area. This list if then filtered to only contain:
   * - Modified basic area elements;
   * - Custom elements.
   *
   * @param id Area id.
   * @returns Flat list of the custom elements and modified basic area elements.
   */
  public getElements(id: Area['_id']): ProjectElement[] {
    const requirements = this.getRequirements(id);
    const elements = [];
    for (const module of requirements) {
      for (const submodule of module.submodules) {
        for (const requirement of submodule.requirements) {
          if (requirement.projectLevel === 5 && requirement.active === true) {
            elements.push(requirement);
          }
        }
      }
    }
    return elements;
  }

  /**
   * Get requirements for an area.
   *
   * To prevent modifying the order (or deleting) the elements from submodules, a new property
   * is added to the submodules, `requirements`. This array of elements are all references, so
   * they can be patched/deleted with the current ElementService.
   *
   * It first filters the 'technicalType' modules, then checks each element's constructionId to
   * be the same as te current area or basicArea. The filtered elements are added to the new list,
   * while duplicate entries (when found for a basicarea and regular area) are available, the area
   * replaces the basicarea element, creating the illusion of 'replacing' the template value.
   *
   * @returns Filtered list of modules.
   */
  public getRequirements(id: Area['_id']): ProjectModule[] {
    const area = this.getArea(id);
    const modules = this.moduleService.get().filter(m => m.isTechnicalType);
    for (const module of modules) {
     for (const submodule of module.submodules) {
        const requirements = [];
        submodule.elements.filter(e => e.active && [area._id, area.basicAreaId].includes(e.constructionId)).forEach(e => {
          const basicAreaIndex = requirements.findIndex(r => r._id === e._id);

          // Overwrite if it already exists in list (but save value as `originalValue`).
          if (basicAreaIndex > -1) {
            requirements[basicAreaIndex] = {
              ...e,
              originalValue: requirements[basicAreaIndex].value
            };

          // Otherwise *add* the requirement.
          } else {
            const templateValue = e.templateValues && e.templateValues.find(t => t.quality === e.quality);
            requirements.push({
              ...e,
              originalValue: templateValue && templateValue.value || null
            });
          }

        });
        submodule.requirements = requirements;
      }
    }
    return modules;
  }

  /**
   * Move area.
   *
   * Move the area either within, or to another cluster.
   *
   * @param area The area to move.
   * @param current The current cluster and index of the area.
   * @param to The new cluster and index of the area.
   */
  public move(area: Area, from: AreaLocation, to: AreaLocation): void {

    // Move item in local data before sending to API, so the user does not have to
    // wait for the api to respond to visually see the drag-drop change.
    let fromCluster = this.projectService.get()
      .buildings.find(b => b._id === area.buildingId)
      .clusters.find(c => c._id === from.id);
    let toCluster = this.projectService.get()
      .buildings.find(b => b._id === area.buildingId)
      .clusters.find(c => c._id === to.id);
    fromCluster.areas.splice(from.index, 1);
    toCluster.areas.splice(to.index, 0, area);
    this.projectService.moveArea(area, fromCluster, toCluster);

    // For some reason, when dropping to a position greater then current position
    // in  the same cluster, to position is off by 1, so that should be fixed.
    if (from.id === to.id && to.index > from.index) {
      to.index++;
    }

    // Update through api.
    this.apiLegacyService.moveArea(area, from, to).subscribe(response => {
      fromCluster = response
        .buildings.find(b => b._id === area.buildingId)
        .clusters.find(c => c._id === from.id);
      toCluster = response
        .buildings.find(b => b._id === area.buildingId)
        .clusters.find(c => c._id === to.id);
      this.projectService.moveArea(area, fromCluster, toCluster);
      this.toastService.open(this.translateService.get('areaMoved'));
    });
  }

  /**
   * Patch the area with multiple values at once.
   *
   * @note Only use this when there's no other option available! I.e. when you need to
   *  patch multiple values which are not known in advance. Because the values can be
   *  *anything*, there's no integrity check when patching values with this method.
   *
   * @todo Merge with the `edit` method from SPEC-2683.
   *
   * @param area The area to patch.
   * @param values A (partial) set of area values to patch.
   * @param config Optional config object.
   * @returns API response.
   */
  public patch(area: Area, values: Partial<Area>, config?: any): Observable<any> {
    return this._patchArea(area, values, config);
  }

  /**
   * Set functionality.
   *
   * @param area The area to set the functionality for.
   * @param functionality The new functionality value.
   */
  public setFunctionality(area: Area, functionality: Area['functionality']): void {
    this._patchArea(area, { functionality: functionality }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Set name.
   *
   * @param area The area to set the name for.
   * @param name The new area name.
   */
  public setName(area: Area, name: Area['name']): void {
    this._patchArea(area, { name: name }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Set quantity.
   *
   * @param area The area to set the quantity for.
   * @param functionality The new quantity value.
   */
  public setQuantity(area: Area, quantity: Area['quantity']): void {
    this._patchArea(area, { quantity: quantity }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Set surface.
   *
   * @param area The area to set the surface for.
   * @param functionality The new surface value.
   */
  public setSurface(area: Area, surface: Area['surface']): void {
    this._patchArea(area, { surface: surface }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Set height.
   *
   * @param area The area to set the height for.
   * @param functionality The new height value.
   */
  public setHeight(area: Area, height: Area['height']): void {
    this._patchArea(area, { height: height }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Set comment.
   *
   * @param area The area to set the comment for.
   * @param functionality The new comment value.
   */
  public setComment(area: Area, comment: Area['comment']): void {
    this._patchArea(area, { comment: comment }).subscribe(() => {
      this.toastService.open(this.translateService.get('areaUpdated'));
    });
  }

  /**
   * Delete area.
   *
   * When deleting an area, all child elements (requirements) should be deleted
   * (deactivated) too.
   *
   * @param area The area to delete.
   * @returns Callback whether the deletion is successful.
   */
  private _delete(area: Area, options: AreaUpdateOptions): Observable<boolean> {
    return new Observable(observer => {
      const elements = this.getElements(area._id);

      // Delete elements (requirements) within the area.
      const deleteElements = () => {
        this.projectService.patchArea(area, { active: false });
        elements.forEach((element, i) => {
          setTimeout(() => {
            this.projectService.patchElement(element, { active: false });
          }, i * 250);
        });
      };

      // If the local flag is set, delete the area/elements only in the local datastore.
      // This is used when deleting a cluster. (the backend takes care of deleting children).
      if (!options.local) {
        this.apiService.deleteArea(area).subscribe(response => {
          deleteElements();
          observer.next(true);
          observer.complete();
        });
      } else {
        deleteElements();
        observer.next(true);
        observer.complete();
      }
    });
  }
}
